import store from "@/store";
import type { Component } from "vue";
import { ContractStatusCodes } from "@upmind-automation/types";
import type { Location, NavigationGuardNext, Route } from "vue-router";
import { AdminRoutes, RegexMatch } from "@/data/enums/router";
import { $bus } from "@/boot/event-bus";
import type { IBrand } from "@upmind-automation/types";
import { BrandConfigKeys } from "@upmind-automation/types";
import _ from "@/boot/lodash";

import CProdsListing from "@/components/app/global/contractProducts/cProdsListing.vue";
import {
  DefaultCProdFilters,
  statusParam,
  IsSubscriptionParam,
  IsOneTimePurchaseParam,
  IsSubscriptionFilter,
  IsOneTimeFilter,
  ClientIdFilter
} from "@/data/filters/contractProducts";

import {
  DefaultCProdSorters,
  StatusSorter,
  DatePurchasedSorter,
  DateCancelledSorter
} from "@/data/sorters/contractProducts";

import CProdProvidersWrapper from "@/components/app/global/contractProducts/cProdProvidersWrapper.vue";

export default [
  {
    path: "products",
    name: "adminClientCProds",
    redirect: { name: "admin__clientSubscriptions" }
  },
  // ─── Subscriptions ───────────────────────────────────────────────────
  {
    path: "products/subscriptions",
    name: "admin__clientSubscriptions",
    redirect: { name: "admin__clientActiveSubscriptions" },
    component: () =>
      import(
        "@/views/admin/clients/client/contractProducts/subscriptions/index.vue"
      ),
    props: () => ({
      topRoutes: require("./menu").clientCProdTabigation
    }),
    meta: {
      backTo: "_.subscriptions",
      title: "_.subscriptions"
    },
    children: [
      {
        path: "active",
        name: "admin__clientActiveSubscriptions",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: DefaultCProdSorters,
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsSubscriptionFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsSubscriptionParam(),

            ...statusParam([
              ContractStatusCodes.ACTIVE,
              ContractStatusCodes.AWAITING_ACTIVATION,
              ContractStatusCodes.PENDING,
              ContractStatusCodes.SUSPENDED
            ])
          })
        }),
        meta: {
          backTo: "_.active_subscriptions",
          title: "_.subscriptions"
        }
      },
      {
        path: "cancelled",
        name: "admin__clientCancelledSubscriptions",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [
            StatusSorter(),
            DateCancelledSorter(),
            DatePurchasedSorter()
          ],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsSubscriptionFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsSubscriptionParam(),

            ...statusParam([
              ContractStatusCodes.CANCELLED,
              ContractStatusCodes.CLOSED
            ])
          })
        }),
        meta: {
          backTo: "_.cancelled_subscriptions",
          title: "_.subscriptions"
        }
      },
      {
        path: "all",
        name: "admin__clientAllSubscriptions",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: DefaultCProdSorters,
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsSubscriptionFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsSubscriptionParam()
          })
        }),
        meta: {
          backTo: "_.all_subscriptions",
          title: "_.subscriptions"
        }
      }
    ]
  },
  // ─── One-time Purchases ──────────────────────────────────────────────
  {
    path: "products/otps",
    name: "admin__clientOTPs",
    redirect: { name: "admin__clientFulfilledOTPs" },
    component: () =>
      import("@/views/admin/clients/client/contractProducts/otps/index.vue"),
    props: () => ({
      topRoutes: require("./menu").clientCProdTabigation
    }),
    meta: {
      backTo: "_.one_time_purchases",
      title: "_.one_time_purchases"
    },
    children: [
      {
        path: "fulfilled",
        name: "admin__clientFulfilledOTPs",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsOneTimeFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsOneTimePurchaseParam(),

            ...statusParam([
              ContractStatusCodes.ACTIVE,
              ContractStatusCodes.AWAITING_ACTIVATION,
              ContractStatusCodes.PENDING
            ])
          })
        }),
        meta: {
          backTo: "_.active_one_time_purchases",
          title: "_.one_time_purchases"
        }
      },
      {
        path: "cancelled",
        name: "admin__clientCancelledOTPs",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [
            StatusSorter(),
            DateCancelledSorter(),
            DatePurchasedSorter()
          ],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsOneTimeFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsOneTimePurchaseParam(),

            ...statusParam([
              ContractStatusCodes.CANCELLED,
              ContractStatusCodes.CLOSED
            ])
          })
        }),
        meta: {
          backTo: "_.cancelled_one_time_purchases",
          title: "_.one_time_purchases"
        }
      },
      {
        path: "all",
        name: "admin__clientAllOTPs",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            IsOneTimeFilter(),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...IsOneTimePurchaseParam()
          })
        }),
        meta: {
          backTo: "_.all_one_time_purchases",
          title: "_.one_time_purchases"
        }
      }
    ]
  },
  // ─── All Products ────────────────────────────────────────────────────
  {
    path: "products/all",
    name: "admin__clientAllProducts",
    redirect: { name: "admin__clientActiveAllProducts" },
    component: () =>
      import("@/views/admin/clients/client/contractProducts/all/index.vue"),
    props: () => ({
      topRoutes: require("./menu").clientCProdTabigation
    }),
    meta: {
      backTo: "_.all",
      title: "_.all_products_and_services"
    },
    children: [
      {
        path: "active",
        name: "admin__clientActiveAllProducts",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...statusParam([
              ContractStatusCodes.ACTIVE,
              ContractStatusCodes.AWAITING_ACTIVATION,
              ContractStatusCodes.PENDING,
              ContractStatusCodes.SUSPENDED
            ])
          })
        }),
        meta: {
          backTo: "_.active_products_and_services",
          title: "_.all_products_and_services"
        }
      },
      {
        path: "cancelled",
        name: "admin__clientCancelledAllProducts",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [
            StatusSorter(),
            DateCancelledSorter(),
            DatePurchasedSorter()
          ],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            ClientIdFilter()
          ],
          routeFilters: () => ({
            ...statusParam([
              ContractStatusCodes.CANCELLED,
              ContractStatusCodes.CLOSED
            ])
          })
        }),
        meta: {
          backTo: "_.cancelled_products_and_services",
          title: "_.all_products_and_services"
        }
      },
      {
        path: "all",
        name: "admin__clientAllAllProducts",
        component: CProdsListing,
        props: (route: Route) => ({
          clientId: route.params.clientId,
          availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
          availableFilters: (brandId: IBrand["id"], vm: any) => [
            ...DefaultCProdFilters([], brandId, vm),
            ClientIdFilter()
          ],
          routeFilters: () => ({})
        }),
        meta: {
          backTo: "_.all_products_and_services",
          title: "_.all_products_and_services"
        }
      }
    ]
  },
  {
    path: `products/:contractId(${RegexMatch.UUID})/:productId(${RegexMatch.UUID})`,
    component: {
      render: (h: any) =>
        h(CProdProvidersWrapper, {
          props: { handleLoading: true },
          scopedSlots: {
            default: () => h("router-view")
          }
        })
    } as Component,
    children: [
      {
        path: "",
        name: "admin__clientCProd",
        /* Note: Smart redirection handled from within component */
        component: () =>
          import(
            "@/views/admin/clients/client/contractProducts/product/index.vue"
          ),
        props: (route: Route) => ({
          contractId: route.params.contractId,
          productId: route.params.productId,
          routes: require("./menu").cProdTabigation
        }),
        meta: {
          title: ({ cProdName }: any) => cProdName
        },
        children: [
          {
            path: "overview",
            name: AdminRoutes.CONTRACT_PRODUCT_OVERVIEW,
            component: () =>
              import(
                "@/components/app/global/contractProducts/cProdOverview.vue"
              ),
            meta: {
              backTo: "_.overview"
            },
            beforeEnter: (
              to: Route,
              from: Route,
              next: NavigationGuardNext
            ) => {
              const doBusReload =
                (!!from.matched.length && to.params.doBusReload) ?? true;
              if (doBusReload) $bus.$emit("cProdProvider::reloadData");
              return next();
            }
          },
          {
            path: "setup",
            name: AdminRoutes.CONTRACT_PRODUCT_SETUP,
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/product/setup/index.vue"
              )
          },
          {
            path: "invoices",
            name: AdminRoutes.CONTRACT_PRODUCT_INVOICES,
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductInvoices.vue"
              ),
            meta: {
              backTo: "_.invoices"
            }
          },
          {
            path: "scheduled-changes",
            name: AdminRoutes.CONTRACT_PRODUCT_SCHEDULED_CHANGES,
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/product/scheduledActions/index.vue"
              ),
            meta: {
              backTo: "_.scheduled_changes"
            }
          },
          {
            path: "credit-notes",
            name: "adminClientCProdCreditNotes",
            component: () =>
              import(
                "@/components/app/global/client/contractProducts/contractProductCreditNotes.vue"
              ),
            meta: {
              backTo: "_.credit_notes"
            }
          },
          {
            path: "settings",
            name: "adminClientCProdSettings",
            component: () =>
              import(
                "@/components/app/admin/client/contractProducts/cProdSettings.vue"
              ),
            meta: {
              backTo: "_.settings"
            }
          },
          {
            path: "activity-log",
            name: "adminClientCProdActivities",
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/activities/index.vue"
              ),
            meta: {
              backTo: "_.activity_log"
            }
          },
          {
            path: "manage",
            name: AdminRoutes.CONTRACT_PRODUCT_PROVISIONING,
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/cProdProvisioning/index.vue"
              ),
            meta: {
              backTo: "_.settings"
            }
          },
          {
            path: "delegates",
            name: "adminClientCProdDelegates",
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/product/delegates/index.vue"
              ),
            meta: {
              backTo: "_.delegates"
            }
          },
          {
            path: "notes",
            name: "adminClientCProdVault",
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/vault/index.vue"
              ),
            meta: {
              backTo: "_.notes_and_secrets"
            },
            beforeEnter: async (to: any, from: any, next: any) => {
              const config = store.getters["brand/config"];
              next(
                _.get(config, BrandConfigKeys.CLIENT_NOTES_AND_SECRETS_ENABLED)
                  ? {}
                  : {
                      name: AdminRoutes.CONTRACT_PRODUCT_OVERVIEW,
                      params: to.params
                    }
              );
            }
          },
          {
            path: "tickets",
            name: AdminRoutes.CONTRACT_PRODUCT_TICKETS,
            component: () =>
              import(
                "@/views/admin/clients/client/contractProducts/tickets/index.vue"
              ),
            meta: {
              backTo: "_.tickets"
            }
          }
        ]
      },
      {
        path: "",
        name: "adminClientCProdQuickLookModal",
        beforeEnter: async (
          to: Route,
          from: Route,
          next: (options: boolean | Location | Location["path"]) => void
        ) => {
          const { clientId, contractId, productId } = to.params;
          if (clientId && contractId && productId) {
            store.dispatch("ui/open/slideModal", {
              config: {
                component: () =>
                  import(
                    "@/components/app/global/contractProducts/cProdQuickLookModal.vue"
                  ),
                canCancel: ["button", "escape", "outside"],
                props: to.params
              }
            });
          }
          store.dispatch("ui/endRouting");
          return next(false);
        }
      }
    ]
  }
];
